'use client';

import Link from 'next/link';
import Layout from '../../components/Layout';
import SubDocsLayout from '../../components/SubDocsLayout';
import useTranslation from '../../lib/translation';
import SEOHead from '../../components/SEOHead';
import { useLocalePath } from '../../lib/useLocalePath';

const TermsOfService = () => {
  const { t, lang } = useTranslation('common');
  const { prefix } = useLocalePath();
  const privacyHref = prefix ? `${prefix}/privacy-policy` : '/privacy-policy';

  return (
    <Layout>
      <SEOHead
        title={
          lang === 'lt'
            ? Array.isArray(t('termsOfService.pageTitle')) ? (t('termsOfService.pageTitle') as string[]).join(' ') : (t('termsOfService.pageTitle') as string)
            : 'Terms of Service | LB Websites'
        }
        description={
          lang === 'lt'
            ? Array.isArray(t('termsOfService.description')) ? (t('termsOfService.description') as string[]).join(' ') : (t('termsOfService.description') as string)
            : 'Review the terms and conditions for using website and CMS services provided by LB Websites.'
        }
        keywords='terms of service, legal, service agreement, payment terms, LB Websites terms'
        canonicalUrl={
          lang === 'lt' ? '/lt/sub/naudojimo-taisykles' : '/sub/terms-of-service'
        }
        lang={lang}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name:
            lang === 'lt'
              ? Array.isArray(t('termsOfService.pageTitle')) ? (t('termsOfService.pageTitle') as string[]).join(' ') : (t('termsOfService.pageTitle') as string)
              : 'Terms of Service | LB Websites',
          description:
            lang === 'lt'
              ? Array.isArray(t('termsOfService.description')) ? (t('termsOfService.description') as string[]).join(' ') : (t('termsOfService.description') as string)
              : 'Review the terms and conditions for using website and CMS services provided by LB Websites.',
          url: `https://www.lbvisible.com${
            lang === 'lt' ? '/lt/sub/naudojimo-taisykles' : '/sub/terms-of-service'
          }`,
          inLanguage: lang === 'lt' ? 'lt' : 'en',
        }}
      />

      <SubDocsLayout>
        <h1 className='text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-4 text-left'>
            {lang === 'lt' ? t('termsOfService.heading') : 'Terms of Service'}
          </h1>
          <p className='text-base text-gray-600 dark:text-gray-300 mb-10 text-left leading-relaxed'>
            {lang === 'lt'
              ? t('termsOfService.subtitle')
              : 'These Terms of Service govern your use of LB Websites, including our website development, CMS platform, hosting, and support services. By placing an order or accessing your admin dashboard, you agree to the following terms:'}
          </p>

          <ul className='space-y-6'>
            <li className='flex gap-4'>
              <span className='flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-gray-400 dark:bg-gray-500' />
              <div>
              <span className='font-semibold text-gray-900 dark:text-white'>
                {lang === 'lt'
                  ? t('termsOfService.sections.services.title')
                  : 'Service scope:'}
              </span>{' '}
              {lang === 'lt'
                ? t('termsOfService.sections.services.content')
                : 'Each package (Beginner, Advanced, Business) includes specific features as described at the time of purchase.'}
              </div>
            </li>
            <li className='flex gap-4'>
              <span className='flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-gray-400 dark:bg-gray-500' />
              <div>
              <span className='font-semibold text-gray-900 dark:text-white'>
                {lang === 'lt'
                  ? t('termsOfService.sections.payment.title')
                  : 'Payment & billing:'}
              </span>{' '}
              {lang === 'lt'
                ? t('termsOfService.sections.payment.content')
                : 'Payments are processed securely via Stripe. Services are delivered upon successful checkout unless otherwise stated.'}
              </div>
            </li>
            <li className='flex gap-4'>
              <span className='flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-gray-400 dark:bg-gray-500' />
              <div>
              <span className='font-semibold text-gray-900 dark:text-white'>
                {lang === 'lt'
                  ? t('termsOfService.sections.intellectualProperty.title')
                  : 'Intellectual property:'}
              </span>{' '}
              {lang === 'lt'
                ? t('termsOfService.sections.intellectualProperty.content')
                : 'All websites are delivered with full rights to the client. Codebase reuse for CMS or backend is permitted by LB Websites.'}
              </div>
            </li>
            <li className='flex gap-4'>
              <span className='flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-gray-400 dark:bg-gray-500' />
              <div>
              <span className='font-semibold text-gray-900 dark:text-white'>
                {lang === 'lt' ? 'Pagalba:' : 'Support:'}
              </span>{' '}
              {lang === 'lt'
                ? 'Nuolatiniai atnaujinimai ir pagalba prieinami pagal jūsų paketą. Atsarginė pagalba prieinama patvirtintiems klientams.'
                : 'Ongoing updates and help are available as outlined in your package. Emergency support is available for verified clients.'}
              </div>
            </li>
            <li className='flex gap-4'>
              <span className='flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-gray-400 dark:bg-gray-500' />
              <div>
              <span className='font-semibold text-gray-900 dark:text-white'>
                {lang === 'lt'
                  ? 'Privatumas ir atitiktis:'
                  : 'Privacy & compliance:'}
              </span>{' '}
              {lang === 'lt'
                ? 'Laikomės ES GDPR įstatymų. Prašome perskaityti mūsų '
                : 'We comply with EU GDPR laws. Please read our '}
              <Link
                href={privacyHref}
                className='text-gray-700 dark:text-gray-200 font-medium underline hover:no-underline'
              >
                {lang === 'lt' ? t('footer.privacyPolicy') : 'Privacy Policy'}
              </Link>{' '}
              {lang === 'lt' ? 'dėl detalių.' : 'for details.'}
              </div>
            </li>
          </ul>

          <p className='mt-10 text-sm text-gray-500 dark:text-gray-400 text-left'>
            {lang === 'lt' ? 'Paskutinį kartą atnaujinta:' : 'Last updated:'}{' '}
            {new Date().toLocaleDateString()}
          </p>
      </SubDocsLayout>
    </Layout>
  );
};

export default TermsOfService;
